/**
 * @description Route component
 * @file
 * @copyright © Copyright IBI Group., created 2019. All use, disclosure, and/or reproduction of this material is prohibited unless authorized in writing. All rights reserved.
 * @module components/Route
 */

import React from 'react';
import PropTypes from 'prop-types';

import * as HeatmapHelpers from '../helpers/HeatmapHelpers';

import ResetContext from '../context/ResetContext';

import Heatmap from './Heatmap';

/**
 * @description The App component.
 * @class
 * @extends {React.Component}
 */
class Route extends React.Component {

    /**
     * @inheritdoc
     */
    constructor(props) {
        super(props);
        const junctions = this.props.junctions.filter(
                junction =>
                    junction.carriageway.split('_')[0] === this.props.route.name
            ),
            links = this.props.links.filter(
                link => link.carriageway.split('_')[0] === this.props.route.name
            ),
            selectedRoutes = HeatmapHelpers.setCarriagewaySelections(
                this.props.selectedCarriageways.filter(
                    route => route.name === this.props.route.name
                ),
                this.props.route
            );

        this.state = {
            route: selectedRoutes,
            junctions: junctions,
            links: links,
            junctionFrom: 0,
            junctionTo: this.props.route.junctions.length - 1,
            zoomLevel: 1
        };

        this.resetMethods = {};
    }

    /**
     * @inheritdoc
     */
    componentDidMount() {
        this.context(this.props.route.id, this.resetComponent.bind(this));
        this.setJunctionAndLinkVisibility();
    }

    /**
     * @inheritdoc
     */
    componentDidUpdate(prevProps, prevState) {
        if (
            prevState.junctionFrom !== this.state.junctionFrom ||
            prevState.junctionTo !== this.state.junctionTo
        ) {
            this.setJunctionAndLinkVisibility();
        }

        if (
            prevProps.selectedCarriageways !== this.props.selectedCarriageways
        ) {
            const selectedRoutes = HeatmapHelpers.setCarriagewaySelections(
                this.props.selectedCarriageways.filter(
                    route => route.name === this.state.route.name
                ),
                this.state.route
            );

            this.setState({
                route: selectedRoutes
            });
        }
    }

    /**
     * @inheritdoc
     */
    componentWillUnmount() {
        this.context(this.props.route.id, this.resetComponent.bind(this), true);
    }

    /**
     * @inheritdoc
     */
    render() {
        const pdfUrl = `${process.env.API_ROOT_URL +
            process.env.JOURNEY_TIME_API_ENDPOINT}/${
            this.state.route.name
        }?date=${this.props.routesPeriods.date}&download=true`;

        return (
            this.state.route.isSelected >= 0 && (
                <ResetContext.Provider
                    value={this.manageResetMethods.bind(this)}
                >
                    <div className='Route'>
                        <div className='Heatmap-title'>
                            <h2>{this.state.route.name}</h2>
                            <div className='form-group'>
                                <label>
                                    Junction
                                    <select
                                        onChange={this.handleJunctionFromChange.bind(
                                            this
                                        )}
                                        value={this.state.junctionFrom}
                                    >
                                        {this.state.route.junctions &&
                                            this.state.route.junctions.map(
                                                (junction, index) => (
                                                    <option
                                                        key={index}
                                                        value={index}
                                                        disabled={
                                                            index >=
                                                            this.state
                                                                .junctionTo
                                                        }
                                                    >
                                                        {junction}
                                                    </option>
                                                )
                                            )}
                                    </select>
                                </label>
                                <label>
                                    to
                                    <select
                                        onChange={this.handleJunctionToChange.bind(
                                            this
                                        )}
                                        value={this.state.junctionTo}
                                    >
                                        {this.state.route.junctions &&
                                            this.state.route.junctions.map(
                                                (junction, index) => (
                                                    <option
                                                        key={index}
                                                        value={index}
                                                        disabled={
                                                            index <=
                                                            this.state
                                                                .junctionFrom
                                                        }
                                                    >
                                                        {junction}
                                                    </option>
                                                )
                                            )}
                                    </select>
                                </label>
                            </div>
                            <div className='form-group'>
                                <label>
                                    Zoom
                                    <input
                                        type='range'
                                        min='1'
                                        max='5'
                                        step='0.05'
                                        value={this.state.zoomLevel}
                                        onChange={this.handleZoomLevelChange.bind(
                                            this
                                        )}
                                    />
                                </label>
                            </div>
                            <span className='Heatmap-title-dash'></span>
                            <div className='form-group'>
                                {this.props.routesPeriods.date !==
                                    HeatmapHelpers.getDateStringFromDate(
                                        new Date()
                                    ) && (
                                    <a
                                        href={pdfUrl}
                                        className='Heatmap-pdf-download'
                                        title='Download as PDF'
                                    >
                                        <span className='material-icons'>
                                            cloud_download
                                        </span>
                                    </a>
                                    )}
                                {this.props.routesPeriods.date ===
                                    HeatmapHelpers.getDateStringFromDate(
                                        new Date()
                                    ) && (
                                    <span
                                        className='Heatmap-pdf-download disabled'
                                        title='PDF is unavailable for the current day'
                                    >
                                        <span className='material-icons'>
                                            cloud_download
                                        </span>
                                    </span>
                                )}

                                <button
                                    className='Heatmap-reset'
                                    onClick={this.resetComponent.bind(this)}
                                    type='button'
                                    aria-label='reset form'
                                >
                                    Reset
                                </button>
                            </div>
                        </div>
                        {this.state.route.carriageways &&
                            this.state.route.carriageways.map(
                                carriageway =>
                                    carriageway.isSelected && (
                                        <Heatmap
                                            key={carriageway.id}
                                            id={carriageway.id}
                                            direction={
                                                carriageway.direction.name
                                            }
                                            periods={this.getVisiblePeriodsForCarriageway(
                                                carriageway.id
                                            )}
                                            isLive={this.props.showLiveData}
                                            links={this.state.links
                                                .filter(
                                                    link =>
                                                        link.carriageway ===
                                                        carriageway.id
                                                )
                                                .sort(
                                                    (a, b) =>
                                                        a.displayOrder -
                                                        b.displayOrder
                                                )}
                                            junctions={this.state.junctions
                                                .filter(
                                                    junction =>
                                                        junction.carriageway ===
                                                        carriageway.id
                                                )
                                                .sort(
                                                    (a, b) =>
                                                        a.displayOrder -
                                                        b.displayOrder
                                                )}
                                            distance={this.state.links
                                                .filter(
                                                    link =>
                                                        link.carriageway ===
                                                        carriageway.id
                                                )
                                                .reduce(
                                                    (a, b) =>
                                                        b.visible
                                                            ? a + b.lengthMetres
                                                            : a,
                                                    0
                                                )}
                                            zoomLevel={this.state.zoomLevel}
                                            openTooltip={this.props.openTooltip}
                                            closeTooltip={
                                                this.props.closeTooltip
                                            }
                                            openJunctionTooltip={
                                                this.props.openJunctionTooltip
                                            }
                                            closeJunctionTooltip={
                                                this.props.closeJunctionTooltip
                                            }
                                            heatmapDragService={
                                                this.props.heatmapDragService
                                            }
                                        ></Heatmap>
                                    )
                            )}
                    </div>
                </ResetContext.Provider>
            )
        );
    }

    /**
     * @description Adds a reset method into the this.resetMethods array.
     * @param {string} id - The id.
     * @param {Function} method - The method to add.
     * @param {boolean} removeMethod - A flag to indicate removing of method.
     */
    manageResetMethods(id, method, removeMethod) {
        this.resetMethods[id] = null;

        if (!removeMethod) {
            this.resetMethods[id] = method;
        }
    }

    /**
     * @description Sets the visibility of junctions and links for this route.
     */
    setJunctionAndLinkVisibility() {
        const visibleJunctionsAndLinks = HeatmapHelpers.getVisibleJunctionsAndLinks(
            this.state.route,
            this.state.junctionFrom,
            this.state.junctionTo
        );

        this.setState({
            ...visibleJunctionsAndLinks
        });
    }

    /**
     * @description Gets all available periods for a particular carriageway, for the selected date.
     * @param {string} carriagewayId - The carriageway Id.
     * @returns {array} - An array of periods.
     */
    getVisiblePeriodsForCarriageway(carriagewayId) {
        if (!this.props.routesPeriods) {
            return [];
        }

        const carriageway = this.props.routesPeriods.carriageways
            ? this.props.routesPeriods.carriageways.find(
                  carriageway => carriageway.id === carriagewayId
              )
            : null;

        return carriageway
            ? carriageway.periods.filter(period => period.isVisible)
            : [];
    }

    /**
     * @description Handles the changing of the from junction.
     * @param {object} event - The event.
     */
    handleJunctionFromChange(event) {
        this.setState({ junctionFrom: parseInt(event.target.value, 10) });
    }

    /**
     * @description Handles the changing of the to junction.
     * @param {object} event - The event.
     */
    handleJunctionToChange(event) {
        this.setState({ junctionTo: parseInt(event.target.value, 10) });
    }

    /**
     * @description Handles the changing of the zoom level.
     * @param {string} zoomLevel - The new value.
     */
    handleZoomLevelChange(event) {
        this.setState({ zoomLevel: parseFloat(event.target.value, 10) });
    }

    /**
     * @description Resets the component to it's default state.
     */
    resetComponent() {
        const state = {
            zoomLevel: 1,
            junctionFrom: 0,
            junctionTo: this.state.route.junctions.length - 1
        };

        this.setState(state);
        Object.keys(this.resetMethods).forEach(methodName => {
            const method = this.resetMethods[methodName];

            method && method();
        });
    }
}

Route.contextType = ResetContext;

Route.defaultProps = {
    route: {},
    showLiveData: true,
    junctions: [],
    links: [],
    selectedCarriageways: [],
    openTooltip: () => null,
    closeTooltip: () => null,
    closeJunctionTooltip: () => null,
    heatmapDragService: {}
};

Route.propTypes = {
    route: PropTypes.object.isRequired,
    showLiveData: PropTypes.bool.isRequired,
    junctions: PropTypes.array.isRequired,
    links: PropTypes.array.isRequired,
    routesPeriods: PropTypes.object.isRequired,
    selectedCarriageways: PropTypes.array.isRequired,
    openTooltip: PropTypes.func.isRequired,
    closeTooltip: PropTypes.func.isRequired,
    openJunctionTooltip: PropTypes.func.isRequired,
    closeJunctionTooltip: PropTypes.func.isRequired,
    heatmapDragService: PropTypes.object.isRequired
};

export default Route;
